/**
 * RESPONSIBILITY: where the weekly bulletin comes from, and the words around it.
 *
 * The bulletin is not uploaded here. Whoever makes it already drops each week's PDF into
 * the church's public Google Drive folder; the site reads that folder and shows the
 * latest upload (R70). So the only thing an admin is ever asked for is the folder — no
 * upload, no override, nothing to remember on a Sunday morning.
 *
 * No @EditableBy, so WRITE defaults to NONE — admin only.
 */
import { Section, Text, Paragraph, Link } from '@framework/core/decorators';
import { SectionName } from '../section-names';
import { ABOVE_HINT } from '../hints';

export const BULLETIN_FOLDER = 'https://drive.google.com/drive/folders/1-CFLiMPYlclQhPjEWA2diuHhGV-CtxWY';

@Section({ name: SectionName.Bulletin, label: 'Bulletin' })
export class BulletinSection {
  @Link({
    label: 'Google Drive folder', default: BULLETIN_FOLDER,
    description:
      'The shared folder the bulletin PDFs are put in. It must be shared as "Anyone with the '
      + 'link". The newest file in it is this week’s bulletin.',
  })
  folderUrl!: string;

  @Text({
    label: 'Link on the home page', maxLength: 120, default: 'This week’s bulletin',
    description: 'Under the welcome on the home page, when the bulletin is for the coming Sunday.',
  })
  homeLabel!: string;

  /** Said instead when the newest bulletin is for an earlier Sunday — never offered as this week's. */
  @Text({
    label: 'When it is an earlier one', maxLength: 120, default: 'Latest bulletin',
    description: 'Used when the newest bulletin in the folder is for a Sunday already past.',
  })
  latestLabel!: string;

  @Text({ label: 'Subtitle', maxLength: 120, default: 'About', description: ABOVE_HINT, group: 'Bulletin page', collapsed: true })
  eyebrow!: string;

  @Text({ label: 'Title', maxLength: 300, default: 'Bulletin', group: 'Bulletin page' })
  heading!: string;

  @Paragraph({ label: 'Description', maxLength: 4000, rows: 2, group: 'Bulletin page' })
  intro!: string;

  @Text({ label: 'Over this week’s', maxLength: 120, default: 'This week', group: 'Bulletin page' })
  currentLabel!: string;

  @Text({ label: 'Open button', maxLength: 60, default: 'Open the bulletin', group: 'Bulletin page' })
  openLabel!: string;

  @Text({ label: 'Over the earlier ones', maxLength: 120, default: 'Earlier bulletins', group: 'Bulletin page' })
  pastHeading!: string;

  @Text({
    label: 'When there is none', maxLength: 300, group: 'Bulletin page',
    default: 'This week’s bulletin will be here soon.',
  })
  emptyText!: string;
}
